import { useState, useCallback } from 'react';
import { formatDateForAPI, addDays } from '../utils/formatters';

/**
 * Pure state hook for date range management.
 * No API calls - just state + comparison window computation.
 */
export function useDateRange() {
  const [dateRange, setDateRangeState] = useState('last7');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);

  // Set a preset date range (clears custom dates)
  const setDateRange = useCallback((preset) => {
    setDateRangeState(preset);
    setStartDate(null);
    setEndDate(null);
    setIsCustomDateOpen(false);
  }, []);

  // Set custom date range atomically
  const setCustomRange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
    setDateRangeState('custom');
    setIsCustomDateOpen(false);
  }, []);

  // Helper: format Date to YYYY-MM-DD (local time)
  const formatYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Compute current and previous comparison windows
  const getComparisonWindows = useCallback(() => {
    let currentStart = null;
    let currentEnd = null;
    const today = new Date();

    switch (dateRange) {
      case 'today': {
        const d = new Date();
        currentStart = formatYMD(d);
        currentEnd = currentStart;
        break;
      }
      case 'yesterday': {
        const y = addDays(new Date(), -1);
        const yStr = formatYMD(y);
        currentStart = yStr;
        currentEnd = yStr;
        break;
      }
      case 'last7': {
        const end = addDays(new Date(), -1);
        const start = addDays(end, -6);
        currentStart = formatYMD(start);
        currentEnd = formatYMD(end);
        break;
      }
      case 'last30': {
        const end = new Date();
        const start = addDays(end, -29);
        currentStart = formatYMD(start);
        currentEnd = formatYMD(end);
        break;
      }
      case 'last90': {
        const end = new Date();
        const start = addDays(end, -89);
        currentStart = formatYMD(start);
        currentEnd = formatYMD(end);
        break;
      }
      case 'thisMonth': {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = now;
        currentStart = formatYMD(start);
        currentEnd = formatYMD(end);
        break;
      }
      case 'lastMonth': {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        currentStart = formatYMD(start);
        currentEnd = formatYMD(end);
        break;
      }
      case 'custom': {
        if (startDate && endDate) {
          currentStart = formatYMD(new Date(startDate));
          currentEnd = formatYMD(new Date(endDate));
        }
        break;
      }
      default: {
        const end = new Date();
        const start = addDays(end, -29);
        currentStart = formatYMD(start);
        currentEnd = formatYMD(end);
      }
    }

    if (!currentStart || !currentEnd) return null;

    // Compute previous window with same inclusive length
    const startObj = new Date(currentStart);
    const endObj = new Date(currentEnd);
    const lengthDays = Math.round((endObj - startObj) / (1000 * 60 * 60 * 24)) + 1;
    const prevEnd = addDays(startObj, -1);
    const prevStart = addDays(prevEnd, -(lengthDays - 1));

    return {
      current: { start: currentStart, end: currentEnd },
      previous: { start: formatYMD(prevStart), end: formatYMD(prevEnd) },
    };
  }, [dateRange, startDate, endDate]);

  // Returns URL query string fragment for API calls
  const getApiDateRangeParams = useCallback(() => {
    if (dateRange === 'custom' && startDate && endDate) {
      const s = formatDateForAPI(startDate);
      const e = formatDateForAPI(endDate);
      return `dateRange=custom&startDate=${s}&endDate=${e}`;
    }
    return `dateRange=${dateRange}`;
  }, [dateRange, startDate, endDate]);

  // Human-readable display text
  const getDateRangeDisplayText = useCallback(() => {
    switch (dateRange) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'last7': return 'Last 7 Days';
      case 'last30': return 'Last 30 Days';
      case 'last90': return 'Last 90 Days';
      case 'all': return 'All Time';
      case 'thisMonth': return 'This Month';
      case 'lastMonth': return 'Last Month';
      case 'custom':
        return startDate && endDate
          ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
          : 'Custom Range';
      default: return 'Last 30 Days';
    }
  }, [dateRange, startDate, endDate]);

  // Label for the currently selected window (e.g., "Sep 8 - Sep 14")
  const getSelectedRangeDatesLabel = useCallback(() => {
    const windows = getComparisonWindows();
    if (!windows) return '';
    const fmt = (str) => new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const sameDay = windows.current.start === windows.current.end;
    if (sameDay) return fmt(windows.current.end);
    return `${fmt(windows.current.start)} \u2013 ${fmt(windows.current.end)}`;
  }, [getComparisonWindows]);

  // Active metric title
  const getDateRangeTitle = useCallback(() => {
    switch (dateRange) {
      case 'today': return "Today's Performance";
      case 'yesterday': return "Yesterday's Performance";
      case 'last7': return 'Last 7 Days Performance';
      case 'last30': return 'Last 30 Days Performance';
      case 'last90': return 'Last 90 Days Performance';
      case 'all': return 'All Time Performance';
      case 'thisMonth': return 'This Month Performance';
      case 'lastMonth': return 'Last Month Performance';
      case 'custom': return 'Custom Range Performance';
      default: return 'Performance';
    }
  }, [dateRange]);

  return {
    dateRange,
    startDate,
    endDate,
    isCustomDateOpen,
    setDateRange,
    setStartDate,
    setEndDate,
    setCustomRange,
    setIsCustomDateOpen,
    getApiDateRangeParams,
    getComparisonWindows,
    getDateRangeDisplayText,
    getSelectedRangeDatesLabel,
    getDateRangeTitle,
  };
}
